import { MRT_ColumnDef } from 'material-react-table';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { PermissionsEnum } from '@/auth/interfaces';
import {
  ROUTER_PATHS,
  TABLE_CONSTANTS_COLUMS,
  emptyCellOneLevel,
  formatDateWithTimeCell,
  hasAllPermissions,
  hasPermission,
} from '@/shared';
import {
  useFetchZones,
  useUpdateZone,
} from '@/shared/actions/erp';
import {
  CustomSearch,
  GeneralTable,
  SingleTableBoxScene,
  SwitchComponente,
} from '@/shared/components/ui';
import {
  useCheckPermissions,
  useTableFilter,
  useTableServerSideFiltering,
} from '@/shared/hooks';
import { useUiConfirmModalStore } from '@/store/ui';
import { RELATED_PERMISSIONS_ZONE } from '@/erp/sytem-sttings/zones/shared/constants';
import { Zone } from '@/shared/interfaces/erp/mantenimiento-operaciones';



export type ZonesPageProps = {};

const ZonesPage: React.FC<ZonesPageProps> = () => {
  useCheckPermissions(PermissionsEnum.api_view_zona);
  const returnUrlZonesPage =
    ROUTER_PATHS.mantenimientoOperaciones.zonasNav;
  const navigate = useNavigate();

  // server side filters - colums table
  const { filterObject, columnFilters, setColumnFilters } =
    useTableServerSideFiltering();

  ///* global state
  const setConfirmDialog = useUiConfirmModalStore(s => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    s => s.setConfirmDialogIsOpen
  );
  ///* table
  const {
    globalFilter,
    pagination,
    searchTerm,
    onChangeFilter,
    setPagination,
  } = useTableFilter();
  const { pageIndex, pageSize } = pagination;

  ///* fetch data
  const {
    data: zonesPagingRes,
    isLoading,
    isRefetching,
  } = useFetchZones({
    page: pageIndex + 1,
    page_size: pageSize,
    nombre: searchTerm,
    ...filterObject,
    filterByState: false,
  });

  ///* mutations
  const updSate = useUpdateZone({
    enableRedirect: false,
  });

  ///* columns
  const columns = useMemo<MRT_ColumnDef<Zone>[]>(
    () => [
      {
        accessorKey: 'nombres',
        header: 'Nombre',
        size: 270,
        Cell: ({ row }) => emptyCellOneLevel(row, 'nombre'),
      },
      /* {
        accessorKey: 'codigo',
        header: 'Código',
        size: 200,
        Cell: ({ row }) => emptyCellOneLevel(row, 'codigo'),
      }, */

      ///* fk
      {
        accessorKey: 'pais_nombre', // to allow filtering
        header: 'País',
        size: 200,
        Cell: ({ row }) => row.original?.pais_data?.nombre || 'N/A',
      },
      {
        accessorKey: 'provincia_nombre',
        header: 'Provincia',
        size: 200,
        Cell: ({ row }) => row.original?.provincia_data?.nombre || 'N/A',
      },
      {
        accessorKey: 'ciudad_nombre',
        header: 'Ciudad',
        size: 200,
        Cell: ({ row }) => row.original?.ciudad_data?.nombre || 'N/A',
      },

      {
        accessorKey: 'cobertura',
        header: 'Cobertura',
        size: 200,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          return (
            <SwitchComponente
              estadoSwitch={row.original?.cobertura}
              titulo={row.original.cobertura ? 'SI' : 'NO'}
              setEstadoSwitch={value => {
                if (!hasPermission(PermissionsEnum.api_change_zona)) return;

                setConfirmDialog({
                  isOpen: true,
                  title: 'Cambiar Estado Cobertura',
                  subtitle:
                    '¿Está seguro que desea cambiar el estado de cobertura de este registro ?',
                  onConfirm: () => {
                    setConfirmDialogIsOpen(false);
                    updSate.mutate({
                      id: row.original?.id_zona!,
                      data: { cobertura: !!value } as any,
                    });
                  },
                });
              }}
            />
          );
        },
      },

      {
        accessorKey: 'estado',
        header: 'Estado',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
          return (
            <SwitchComponente
              estadoSwitch={row.original.estado}
              titulo={row.original.estado ? 'Activo' : 'Inactivo'}
              setEstadoSwitch={state => {
                if (!hasPermission(PermissionsEnum.api_change_zona)) return;

                setConfirmDialog({
                  isOpen: true,
                  title: 'Cambiar Estado',
                  subtitle:
                    '¿Está seguro que desea cambiar el estado de este registro ?',
                  onConfirm: () => {
                    setConfirmDialogIsOpen(false);
                    updSate.mutate({
                      id: row.original.id_zona!,
                      data: { estado: !!state } as any,
                    });
                  },
                });
              }}
            />
          );
        },
      },
      {
        accessorKey: 'fecha_creacion',
        header: 'Fecha de Creación',
        size: 200,
        enableColumnFilter: true,
        filterVariant: 'date',
        enableSorting: false,
        Cell: ({ row }) => formatDateWithTimeCell(row, 'fecha_creacion'),
      },
      {
        accessorKey: 'fecha_modificacion',
        header: 'Fecha de Modificación',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => formatDateWithTimeCell(row, 'fecha_modificacion'),
      },
    ],
    []
  );

  ///* handlers
  const onEdit = (row: Zone) => {
    navigate(`${returnUrlZonesPage}/editar/${row.id_zona}`);
  };

  // const onDelete = (original: Zone) => {
  //   setConfirmDialog({
  //     isOpen: true,
  //     subtitle: '¿Está seguro que desea eliminar la Zona seleccionada?',
  //     title: 'Confirmar eliminación',
  //     onConfirm: () => {
  //       deleteZoneMutation.mutate(original.id_zona!);
  //     },
  //     onClose() {
  //       setConfirmDialogIsOpen(false);
  //     },
  //   });
  // };

  return (
    <SingleTableBoxScene
      title="Zonas"
      urlRedirect={`${returnUrlZonesPage}/crear`}
      enableRedirect={hasPermission(PermissionsEnum.api_add_zona)}
    >
      <CustomSearch
        onChange={onChangeFilter}
        value={globalFilter}
        text="por nombre"
      />

      <GeneralTable<Zone>
        columns={columns}
        data={zonesPagingRes?.data?.data || []}
        //
        isLoading={isLoading}
        isRefetching={isRefetching}
        // // search
        enableGlobalFilter={false}
        // // filters - llega al back
        enableManualFiltering={true}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        // // pagination
        pagination={pagination}
        onPaging={setPagination}
        rowCount={zonesPagingRes?.data?.count}
        enableSorting={false}
        // // actions ========
        enableColumnActions={hasAllPermissions([
          PermissionsEnum.api_change_zona,
          ...RELATED_PERMISSIONS_ZONE,
        ])}
        actionsColumnSize={TABLE_CONSTANTS_COLUMS.ACTIONCOLUMN_WIDTH}
        // crud
        canEdit={hasAllPermissions([
          PermissionsEnum.api_change_zona,
          ...RELATED_PERMISSIONS_ZONE,
        ])}
        onEdit={onEdit}
        canDelete={false}
      />

    </SingleTableBoxScene>
  );
};

export default ZonesPage;